import React, { useEffect, useMemo, useState } from 'react'
import { HiAdjustments, HiChevronDown, HiChevronRight, HiSearch } from 'react-icons/hi'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import {
    cloneRole,
    createRole,
    deleteRole,
    getPermissionCatalog,
    getRolePermissions,
    listRoles,
    replaceRolePermissions,
    type IPermission,
    type IRole,
} from '../../api/security'
import { getLocale } from '../../locale'
import {
    PERMISSION_GROUP_LABELS,
    PERMISSION_GROUP_ORDER,
    getPermissionLabel,
} from './permission-labels'

const styles = {
    checkedBg: { backgroundColor: '#eef2ff' },
    checkedBgDark: { backgroundColor: '#111827' },
}

type GroupKey = string

export const RolesPermissionsPage: React.FC = () => {
    const isHebrew = getLocale() === 'he'
    const [loading, setLoading] = useState(true)
    const [roles, setRoles] = useState<IRole[]>([])
    const [permissions, setPermissions] = useState<IPermission[]>([])
    const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
    const [newRoleName, setNewRoleName] = useState('')
    const [selectedRolePermissionKeys, setSelectedRolePermissionKeys] = useState<string[]>([])
    const [search, setSearch] = useState('')
    const [collapsedGroups, setCollapsedGroups] = useState<Set<GroupKey>>(new Set())
    const [isDarkMode, setIsDarkMode] = useState(false)

    useEffect(() => {
        const checkDark = () => setIsDarkMode(document.documentElement.classList.contains('dark'))
        checkDark()
        const observer = new MutationObserver(checkDark)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        appRef.setHeader(
            <HiAdjustments className="me-2" size={28} />,
            () => (isHebrew ? 'תפקידים והרשאות' : 'Roles & Permissions'),
        )
    }, [isHebrew])

    const load = async () => {
        setLoading(true)
        try {
            const [rolesData, permissionsData] = await Promise.all([listRoles(), getPermissionCatalog()])
            setRoles(rolesData.data)
            setPermissions(permissionsData.data)
            if (rolesData.data.length > 0 && selectedRoleId === null) {
                setSelectedRoleId(rolesData.data[0].id)
            }
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'טעינת תפקידים נכשלה' : 'Failed to load roles'))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (selectedRoleId == null) return
            ; (async () => {
                try {
                    const rolePermissions = await getRolePermissions(selectedRoleId)
                    setSelectedRolePermissionKeys([...(rolePermissions.data || [])])
                } catch {
                    setSelectedRolePermissionKeys([])
                }
            })()
    }, [selectedRoleId])

    const onCreateRole = async () => {
        if (!newRoleName.trim()) return
        try {
            const created = await createRole(newRoleName.trim())
            setNewRoleName('')
            setSelectedRoleId(created.data.id)
            toast.success(isHebrew ? 'התפקיד נוצר' : 'Role created')
            await load()
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'יצירת תפקיד נכשלה' : 'Failed to create role'))
        }
    }

    const saveRolePermissions = async () => {
        if (selectedRoleId == null) return
        try {
            await replaceRolePermissions(selectedRoleId, selectedRolePermissionKeys)
            toast.success(isHebrew ? 'הרשאות תפקיד נשמרו' : 'Role permissions saved')
        } catch (error: any) {
            toast.error(error?.message || (isHebrew ? 'שמירה נכשלה' : 'Save failed'))
        }
    }

    const toggleGroup = (group: GroupKey) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev)
            if (next.has(group)) next.delete(group)
            else next.add(group)
            return next
        })
    }

    // ── Build grouped, filtered permissions ─────────────────────────

    const { groups, filteredCount, totalCount } = useMemo(() => {
        const searchLower = search.toLowerCase().trim()

        const grouped = new Map<GroupKey, IPermission[]>()
        for (const perm of permissions) {
            let groupKey: string
            if (perm.key.startsWith('settings.ltsp101')) groupKey = 'settings.ltsp101'
            else if (perm.key.startsWith('settings.fragmented')) groupKey = 'settings.fragmented'
            else if (perm.key.startsWith('settings')) groupKey = 'settings'
            else if (perm.key.startsWith('module.')) groupKey = 'module'
            else groupKey = perm.key.split('.')[0]
            if (!grouped.has(groupKey)) grouped.set(groupKey, [])
            grouped.get(groupKey)!.push(perm)
        }

        const groupOrder = new Map(PERMISSION_GROUP_ORDER.map((k, i) => [k, i]))
        const sortedGroups = Array.from(grouped.keys()).sort((a, b) => {
            const ai = groupOrder.get(a) ?? 999
            const bi = groupOrder.get(b) ?? 999
            if (ai !== bi) return ai - bi
            return a.localeCompare(b)
        })

        const result: { groupKey: GroupKey; items: IPermission[] }[] = []
        let fCount = 0
        let tCount = 0

        for (const groupKey of sortedGroups) {
            const items = grouped.get(groupKey)!
            tCount += items.length
            if (!searchLower) {
                result.push({ groupKey, items })
                fCount += items.length
                continue
            }
            const filtered = items.filter(perm => {
                const label = getPermissionLabel(perm.key, isHebrew ? 'he' : 'en').toLowerCase()
                return perm.key.toLowerCase().includes(searchLower) || label.includes(searchLower)
            })
            if (filtered.length > 0) {
                result.push({ groupKey, items: filtered })
                fCount += filtered.length
            }
        }

        return { groups: result, filteredCount: fCount, totalCount: tCount }
    }, [permissions, search, isHebrew])

    const selectedGroupKeys = useMemo(
        () => new Set(selectedRolePermissionKeys),
        [selectedRolePermissionKeys],
    )

    const togglePermission = (key: string) => {
        setSelectedRolePermissionKeys(prev =>
            prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key],
        )
    }

    const selectAllInGroup = (keys: string[]) => {
        setSelectedRolePermissionKeys(prev => {
            const next = new Set(prev)
            for (const k of keys) next.add(k)
            return Array.from(next)
        })
    }

    const deselectAllInGroup = (keys: string[]) => {
        const keySet = new Set(keys)
        setSelectedRolePermissionKeys(prev => prev.filter(k => !keySet.has(k)))
    }

    const countSelectedInGroup = (keys: string[]) =>
        keys.filter(k => selectedGroupKeys.has(k)).length

    // ── Render ─────────────────────────────────────────────────────

    if (loading) {
        return <div className="text-gray-600 dark:text-gray-300">{isHebrew ? 'טוען...' : 'Loading...'}</div>
    }

    return (
        <section className="rounded-xl bg-white dark:bg-gray-800 shadow p-5 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {isHebrew ? 'תפקידים והרשאות' : 'Roles & Permissions'}
            </h2>
            <div className="grid md:grid-cols-3 gap-3">
                <input
                    className="rounded border border-gray-300 px-3 py-2 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                    placeholder={isHebrew ? 'שם תפקיד חדש' : 'New role name'}
                    value={newRoleName}
                    onChange={event => setNewRoleName(event.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') onCreateRole() }}
                />
                <button className="rounded bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2" onClick={onCreateRole}>
                    {isHebrew ? 'צור תפקיד' : 'Create Role'}
                </button>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
                {roles.map(role => (
                    <div
                        key={role.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRoleId(role.id)}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                setSelectedRoleId(role.id)
                            }
                        }}
                        className={`rounded border p-3 bg-white dark:bg-gray-900 cursor-pointer ${selectedRoleId === role.id
                            ? 'border-indigo-500 ring-1 ring-indigo-500'
                            : 'border-gray-200 dark:border-gray-700'
                            }`}
                    >
                        <div className="flex justify-between items-center">
                            <div className="text-start font-semibold text-gray-900 dark:text-gray-100">{role.name}</div>
                            {!role.isSystem && (
                                <div className="flex items-center gap-3 text-sm">
                                    <button
                                        className="text-indigo-600 hover:underline"
                                        onClick={async event => {
                                            event.stopPropagation()
                                            const name = window.prompt(isHebrew ? 'שם תפקיד חדש לשכפול' : 'New role name for clone')
                                            if (!name) return
                                            try {
                                                await cloneRole(role.id, name)
                                                await load()
                                            } catch (error: any) {
                                                toast.error(error?.message || 'Clone failed')
                                            }
                                        }}
                                    >
                                        {isHebrew ? 'שכפל' : 'Clone'}
                                    </button>
                                    <button
                                        className="text-red-600 hover:underline"
                                        onClick={async event => {
                                            event.stopPropagation()
                                            if (!window.confirm(isHebrew ? 'למחוק תפקיד?' : 'Delete role?')) return
                                            try {
                                                await deleteRole(role.id)
                                                await load()
                                            } catch (error: any) {
                                                toast.error(error?.message || 'Delete failed')
                                            }
                                        }}
                                    >
                                        {isHebrew ? 'מחק' : 'Delete'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {selectedRoleId != null && (
                <div className="rounded p-3 bg-white dark:bg-gray-800 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                            {isHebrew ? 'הרשאות תפקיד' : 'Role Permissions'}
                        </div>
                        {search && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {isHebrew
                                    ? `${filteredCount} מתוך ${totalCount} הרשאות`
                                    : `${filteredCount} of ${totalCount} permissions`}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
                        <HiSearch className="ms-3 text-gray-400 shrink-0" size={18} />
                        <input
                            className="w-full border-none bg-transparent px-2 py-2 text-gray-900 dark:text-gray-100 outline-none"
                            placeholder={isHebrew ? 'חיפוש הרשאות...' : 'Search permissions...'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>

                    {groups.length === 0 ? (
                        <div className="text-gray-500 dark:text-gray-400 text-sm py-4 text-center">
                            {isHebrew ? 'לא נמצאו הרשאות התואמות לחיפוש' : 'No permissions match your search'}
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-[70vh] overflow-y-auto border border-gray-300 dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-900">
                            {groups.map(({ groupKey, items }) => {
                                const groupLabel =
                                    PERMISSION_GROUP_LABELS[groupKey]?.[isHebrew ? 'he' : 'en'] ?? groupKey
                                const isCollapsed = collapsedGroups.has(groupKey)
                                const selectedInGroup = countSelectedInGroup(items.map(p => p.key))
                                const allSelected = selectedInGroup === items.length
                                const groupAction = allSelected
                                    ? () => deselectAllInGroup(items.map(p => p.key))
                                    : () => selectAllInGroup(items.map(p => p.key))

                                return (
                                    <div key={groupKey} className="border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => toggleGroup(groupKey)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault()
                                                    toggleGroup(groupKey)
                                                }
                                            }}
                                            className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm select-none"
                                        >
                                            <div className="flex items-center gap-2">
                                                {isCollapsed ? <HiChevronRight /> : <HiChevronDown />}
                                                <span className="font-medium text-gray-800 dark:text-gray-100">{groupLabel}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">({selectedInGroup}/{items.length})</span>
                                            </div>
                                            <button
                                                onClick={e => { e.stopPropagation(); groupAction() }}
                                                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                                            >
                                                {allSelected
                                                    ? (isHebrew ? 'בטל הכל' : 'Deselect all')
                                                    : (isHebrew ? 'בחר הכל' : 'Select all')}
                                            </button>
                                        </div>
                                        {!isCollapsed && (
                                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                                {items.map(perm => {
                                                    const label = getPermissionLabel(perm.key, isHebrew ? 'he' : 'en')
                                                    const checked = selectedGroupKeys.has(perm.key)
                                                    return (
                                                        <div
                                                            key={perm.id}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => togglePermission(perm.key)}
                                                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePermission(perm.key) } }}
                                                            className="flex items-start gap-2 px-3 py-1.5 text-sm cursor-pointer"
                                                            style={checked ? (isDarkMode ? styles.checkedBgDark : styles.checkedBg) : undefined}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="mt-0.5 accent-indigo-500"
                                                                checked={checked}
                                                                onChange={() => togglePermission(perm.key)}
                                                            />
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="text-gray-800 dark:text-gray-100 truncate">{label}</span>
                                                                <code className="text-xs text-gray-400 dark:text-gray-500 truncate">{perm.key}</code>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    <button
                        className="mt-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 disabled:opacity-50"
                        onClick={saveRolePermissions}
                        disabled={selectedRoleId == null}
                    >
                        {isHebrew ? 'שמור הרשאות תפקיד' : 'Save Role Permissions'}
                    </button>
                </div>
            )}
        </section>
    )
}
